<template>
  <WithBackdrop
    :backdrop="data?.fields?.backdrop"
    :class="[$style.config, $style.wrap]"
  >
    <BaseContainer :max-width="640">
      <header :class="$style.header">
        <BaseHeading size="h2" :class="$style.heading">
          {{ data?.fields?.header }}
        </BaseHeading>
      </header>

      <MarkedParser
        :class="[$style.copy, 'u-type-p3']"
        :md="data?.fields?.copy"
      />

      <div v-if="data?.fields?.label" :class="[$style.label, 'u-type-h5']">
        {{ data?.fields?.label }}
      </div>
    </BaseContainer>

    <BaseContainer :max-width="1200">
      <div ref="swiper" :class="['swiper', $style.timelines]">
        <div :class="'swiper-wrapper'">
          <div
            v-for="(timeline, index) in data?.fields?.timeline"
            :key="timeline?.sys?.id"
            :class="['swiper-slide', $style.timeline]"
          >
            <div
              :class="[
                $style.event,
                index < currentEventIndex && $style.pastEvent,
                index > currentEventIndex && $style.futureEvent,
                index === currentEventIndex && $style.currentEvent,
              ]"
            >
              <span :class="$style.date">{{ timeline?.fields?.date }}</span>
              <br />
              <i
                aria-hidden="true"
                :class="[
                  $style.dot,
                  index === 0 && $style.start,
                  index === (data?.fields?.timeline?.length || 0) - 1 &&
                    $style.end,
                  timeline?.sys?.id === computedCurrentEvent?.sys?.id &&
                    $style.filled,
                ]"
              >
                <span :class="$style.dotLine" />
              </i>
              <NuxtLinkLocale
                :to="timeline?.fields?.link"
                :class="$style.title"
                target="_blank"
              >
                {{ timeline?.fields?.title }}
              </NuxtLinkLocale>
            </div>
          </div>
        </div>
      </div>
    </BaseContainer>
  </WithBackdrop>
</template>

<script setup lang="ts">
import Swiper from 'swiper'
import { isBefore } from 'date-fns'

import type { TypeProductTimelineWithoutUnresolvableLinksResponse } from '~/types/contentful'

import 'swiper/css'

const props = defineProps<{
  data: TypeProductTimelineWithoutUnresolvableLinksResponse
}>()

const computedCurrentEvent = computed(() => {
  const today = new Date()
  const reversedTimeline = props.data?.fields?.timeline?.slice?.()?.reverse?.() // so that the most in the future events are at the start of the array

  const currentEvent = reversedTimeline?.find?.((t) => {
    const eventDate = new Date(t?.fields?.datetime || '')
    return isBefore(eventDate, today)
  })

  return currentEvent || props.data?.fields?.timeline?.[0]
})

const currentEventIndex = ref(0)
const swiperElRef = useTemplateRef<HTMLElement>('swiper')
const swiperInstance = ref<null | Swiper>(null)
const initilizeSwiper = () => {
  if (swiperElRef.value) {
    swiperInstance.value = new Swiper(swiperElRef.value, {
      slidesPerView: 'auto',
      centeredSlides: true,
      spaceBetween: 0,
      breakpoints: {
        768: {
          centeredSlides: false,
        },
      },
      on: {
        init: () => {
          const eventIndex =
            props.data?.fields?.timeline?.findIndex?.(
              (t) => t?.sys?.id === computedCurrentEvent.value?.sys?.id,
            ) || 0
          currentEventIndex.value = eventIndex
          swiperInstance.value?.slideTo?.(eventIndex)
        },
        slideChange: () => {
          currentEventIndex.value = swiperInstance.value?.activeIndex || 0
        },
      },
    })
  }
}

onMounted(() => {
  initilizeSwiper()
})
</script>

<style lang="scss" module>
.config {
  --wrap-padding--base: 40px 0 60px;
  --wrap-padding--sm: 60px 0 80px;
  --wrap-padding--md: 70px 0 100px;
  --wrap-padding: var(--wrap-padding--base);

  @include media('sm') {
    --wrap-padding: var(--wrap-padding--sm);
  }

  @include media('md') {
    --wrap-padding: var(--wrap-padding--md);
  }
}

.wrap {
  padding: var(--wrap-padding);
}

.header {
  text-align: center;
  margin-bottom: 1em;
}

.label {
  font-family: var(--strong-font-family);
  font-weight: bold;
  text-align: center;
  margin: 1em auto;
}

.copy {
  text-align: center;
}

.timelines {
  margin: 2em auto;

  .timeline {
    text-align: center;
    width: 240px;
  }
}

.event {
  height: 100%;

  .title {
    height: calc(100% - 80px);
  }
}

.pastEvent {
  .date,
  .title {
    filter: grayscale(100%);
    opacity: 0.4;
  }

  .title {
    background: var(--past-event-background, undefined);
    border: 1px solid var(--past-event-border, var(--black));
    color: var(--past-event-color, var(--black));
  }
}

.futureEvent {
  .title {
    background: var(--future-event-background, var(--white));
    border: 1px solid var(--future-event-border, var(--purple));
    color: var(--future-event-color, var(--black));
  }
}

.currentEvent {
  .title {
    background: var(--current-event-background, var(--purple));
    border: 1px solid var(--current-event-border, var(--purple));
    color: var(--current-event-color, var(--white));
  }
}

.date {
  display: inline-block;
  margin-bottom: 1em;
}

.title {
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;

  background: var(--purple);

  display: grid;
  min-height: 5em;
  place-content: center;

  padding: 1em 1em 0.9em;
  margin: 0 0.5em;

  filter: saturate(2);
}

.dot {
  background: var(--white);

  display: block;
  position: relative;

  width: 24px;
  height: 24px;
  margin: 0 auto 20px;

  border: 1px solid var(--purple);
  border-radius: 50%;

  .currentEvent & {
    background: var(--current-event-background, var(--purple));
  }

  .pastEvent & {
    background: var(--black);
  }

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;

    width: 120px; // half of the width of the timeline
    height: 1px;
    background: var(--purple);
    z-index: -1;
  }

  &::before {
    left: unset;
    right: 50%;
  }

  &.start::before {
    display: none;
  }

  &.end::after {
    display: none;
  }

  .dotLine {
    position: absolute;
    top: 100%;
    left: 50%;

    background: var(--purple);
    width: 1px;
    height: 21px;
  }
}
</style>
